// Visit details dialog (OperationManagement/VisitModal and its sub-dialogs).
// Toasts and validation messages live in the action code and stay Turkish, as
// before; only on-screen texts are here.
const visitModal = {
  title: 'Visit Details',
  subtitleFallback: 'Field operation',
  subtitle: {
    patient: 'Patient {{id}}',
    visitNo: 'Visit {{n}}',
  },
  modes: {
    completed: 'Completed',
    restricted: 'View and delete',
    readOnly: 'Read-only',
    adminEditable: 'Admin · editable',
    mirror: 'Hybrid mirror visit',
  },
  notices: {
    restricted: 'With this account you can only view and delete visits; changes can’t be saved.',
    readOnly: 'The completion period of this visit has ended (last day + 72 hours). You can view the details but not change them. Ask your manager if something needs correcting.',
    adminEditable: 'The completion period of this visit has ended. As an admin you can still edit and save it.',
    mirror: 'Hybrid project mirror visit: this visit was added to your calendar automatically, together with the visit opened by {{name}}.',
    mirrorDefaultName: 'the blinded employee',
    mirrorSourceDeleted: 'The source visit was deleted later.',
    pendingTitle: 'Missing completion date: {{items}}',
    pendingText: 'This visit is completed but these dates haven’t been entered yet. Enter them in “D — Completion Dates” on the “Visit Evaluation” step and save; the warning then disappears.',
    pendingAction: 'Go to section D',
    deadlineTitle: 'Visit period has ended',
    deadlineText: 'Visit ended: {{end}} · Complete by: {{deadline}}',
    deadlineHours_one: '{{count}} hour left',
    deadlineHours_other: '{{count}} hours left',
  },
  progress: {
    title: 'Progress',
    percent: '{{value}}%',
    ready: 'Ready to complete',
    readyHint: 'All required fields are filled in. Close the visit with “Complete Visit”.',
    missing_one: '{{count}} field missing to complete',
    missing_other: '{{count}} fields missing to complete',
    missingHint: 'Orange steps have fields that still need to be filled in.',
    stepOf: 'Step {{current}} of {{total}}',
    stepsLabel: 'Visit steps',
    autosave: 'Saved instantly',
  },
  status: {
    done: 'Done',
    missing_one: '{{count}} missing',
    missing_other: '{{count}} missing',
    optional: 'Optional',
    info: 'Info',
    records_one: '{{count}} record',
    records_other: '{{count}} records',
    missingList: 'Missing: {{items}}',
    missingFields: 'Missing fields:',
  },
  steps: {
    informations: {
      label: 'Details',
      short: 'Project and patient',
      title: 'Project and patient details',
      help: 'The project and patient this visit belongs to. These details come from the project record, so there is nothing to change here. Follow the steps below in order to complete the visit record.',
    },
    todoList: {
      label: 'To-do',
      short: 'Checks before and after the visit',
      title: 'To-do list',
      help: 'Tick the checks to be done before and after the visit. At least one item must be ticked before the visit can be completed.',
    },
    visitForm: {
      label: 'Visit Form',
      short: 'Type, times, patient and place',
      title: 'Visit form',
      help: 'Enter the visit type, the times, the patient and where the visit took place. Fields marked with * must be filled in before the visit can be completed. Save your changes with the “Save” button below.',
    },
    stock: {
      label: 'Stock',
      short: 'Materials used',
      title: 'Materials used',
      help: 'Add the materials you used during the visit. The quantity is deducted from the stock assigned to you right away and returned to it if you remove the line.',
    },
    shipments: {
      label: 'Shipments',
      short: 'Parcels sent',
      title: 'Shipment tracking',
      help: 'Track the samples, documents or materials you shipped for this visit. Add the tracking number and shipping papers, and update the status with a single tap.',
    },
    expenses: {
      label: 'Expenses',
      short: 'Travel, accommodation, meals…',
      title: 'Expenses',
      help: 'Add the travel, accommodation, meal and other expenses of this visit together with the receipt or invoice.',
    },
    visitEvaluation: {
      label: 'Visit Evaluation',
      short: 'Window, process and outcome',
      title: 'Visit evaluation',
      help: 'Fill in the evaluation of the visit section by section. Fields marked with * are required before the visit can be completed. Save your changes with “Save”.',
      helpHome: 'Home visit evaluation: visit window, travel, visit duration and source document dates.',
      helpSite: 'Hospital (site) visit evaluation. “D — Completion Dates” doesn’t block completing the visit; the dates can be entered later.',
    },
    craApproval: {
      label: 'CRA Approval',
      short: 'Approval e-mail to the CRA',
      title: 'CRA approval',
      help: 'Send the visit details to the CRA you choose for approval by e-mail.',
    },
  },
  actions: {
    close: 'Close',
    back: 'Back',
    next: 'Next',
    nextTo: 'Next step: {{step}}',
    backTo: 'Previous step: {{step}}',
    save: 'Save',
    saving: 'Saving...',
    complete: 'Complete Visit',
    completeShort: 'Complete',
    completing: 'Completing...',
    delete: 'Delete Visit',
    deleting: 'Deleting...',
    cascadeDelete: 'Delete This and Later Visits',
  },
  confirmDelete: {
    title: 'Delete this visit?',
    message: 'Are you sure you want to delete this visit? This can’t be undone.',
    confirm: 'Yes, delete',
    cancel: 'Cancel',
  },
  common: {
    yes: 'Yes',
    no: 'No',
    select: 'Select',
    pleaseSelect: 'Please select',
    notSpecified: 'Not specified',
    missing: 'Missing',
    hours: 'Hours',
    minutes: 'Minutes',
    km: 'km',
    days_one: '{{count}} day',
    days: '{{count}} days',
    cancel: 'Cancel',
    readOnlyField: 'Filled in automatically.',
  },
  info: {
    project: 'Project name',
    projectMissing: 'Project details couldn’t be loaded',
    phase: 'Project phase',
    centersType: 'Project coordinating centre type',
    patientId: 'Patient ID',
    visitNumber: 'Visit number',
    visitNumberValue: 'Visit {{n}}',
    roadmapTitle: 'The steps of this visit',
    roadmapHint: 'Tap a step to go straight to it. Orange steps have fields that must be filled in before the visit can be completed.',
  },
  todo: {
    autosaveNote: 'Ticked items are saved instantly; you don’t need to press “Save” for this page.',
    counter: '{{done}} of {{total}} items ticked',
    groups: {
      pre: {
        title: 'Before the visit',
        hint: 'Check these before heading out to the visit.',
      },
      visit: {
        title: 'During and after the visit',
        hint: 'Check these during and after the visit.',
      },
    },
    items: {
      pre_patientCalled: 'Has the patient been called?',
      pre_nurseTransferArranged: 'Has the nurse’s transport (transfers) been arranged?',
      pre_courierArranged: 'Have a courier and courier supplies (ice, dry ice, boxes, etc.) been arranged for the samples to be collected?',
      pre_hospitalApproval: 'Has the hospital approved the shipment of the drug?',
      pre_nurseEquipmentReady: 'Has the nurse prepared the equipment for the visit?',
      pre_accommodationPlanned: 'Has accommodation been planned, if needed?',
      patientConsent: 'Has the patient consent form for the home visit been obtained?',
      protocolUpdate: 'Is there a protocol update? If so, have new consents been obtained?',
      kvkkApproval: 'Has the KVKK (data protection) notice been acknowledged?',
      visitNumberCheck: 'Has the visit number been checked?',
      projectCheck: 'Have the project details been checked?',
      workflowCheck: 'Has the workflow been followed up?',
      adverseEventCheck: 'If there is an adverse event, has it been classified, followed up and reported?',
      medicationCheck: 'Has concomitant medication been followed up?',
      sourceDocuments: 'Have the source documents been filled in?',
      sourceDocumentsApproval: 'Has the sponsor project manager approved sharing the source documents filled in during the visit?',
      sourceDocumentsShared: 'Have the source documents filled in during the visit been shared with the study team by e-mail?',
      transferForm: 'Has the transfer form been filled in?',
      documentShipping: 'Have the source documents been shipped?',
      stockEntryRequired: 'Will a stock entry be made?',
    },
    stockHint: 'If ticked, the visit can’t be completed until at least one material is entered on the Stock step.',
    goToStock: 'Go to the Stock step',
    stockDone_one: '{{count}} material entered',
    stockDone_other: '{{count}} materials entered',
    stockPending: 'No material entered yet — the visit can’t be completed',
  },
  form: {
    sections: {
      type: {
        title: 'Visit type and source document',
        hint: 'Choose how the visit was done and whether a source document was filled in.',
      },
      time: {
        title: 'Date and time',
        hint: 'Enter the times the visit started and ended.',
      },
      patient: {
        title: 'Patient and physician',
        hint: 'Enter the patient you visited, the visit number and the responsible physician.',
      },
      location: {
        title: 'Location and travel',
        hint: 'The city is filled in when you choose the hospital.',
      },
      notes: {
        title: 'Notes',
        hint: 'Anything else you want to add about the visit.',
      },
    },
    visitType: 'Visit type',
    visitTypes: {
      hastane: 'Hospital Visit',
      home: 'Home Visit',
      telefon: 'Phone Visit',
      unplanning: 'Unplanned Visit',
      handover: 'Handover Visit',
    },
    screenFail: 'Screen fail?',
    screenFailHint: 'Tick if the patient was found ineligible during screening.',
    screenFailNote: 'Records marked as screen fail don’t count as visits; they are recorded as work done.',
    sourceDocument: 'Source document',
    sourceDocumentTime: 'Source document completion time',
    startDate: 'Visit start date',
    startDateHint: 'The date is set when the visit is planned and can’t be changed here.',
    startTime: 'Visit start time',
    endDate: 'Visit end date',
    endDateHint: 'Can’t be changed once saved.',
    endTime: 'Visit end time',
    patientId: 'Patient ID',
    patientsLoading: 'Loading patients...',
    patientSelect: 'Select a patient',
    patientManual: 'You have no registered patients - you can type the ID',
    patientHint: 'Only your own patients and patients you have visited before are listed.',
    visitNumber: 'Visit number',
    visitNumberPlaceholder: 'E.g. 1, 2, 3...',
    doctor: 'Physician',
    doctorPlaceholder: 'Physician’s full name',
    hospital: 'Hospital',
    hospitalsLoading: 'Loading hospitals...',
    myCenters: 'Your centres on this project',
    otherCenters: 'Other centres',
    centers: 'Centres',
    autoFilledProject: 'Filled in from your project assignment; you can change it.',
    autoFilledPrevious: 'Filled in from your last visit; you can change it.',
    city: 'City',
    citiesLoading: 'Loading cities...',
    cityHint: 'Updated automatically when you choose the hospital.',
    travelType: 'Travel type',
    travelTypes: {
      local: 'Within the city',
      outside: 'Out of town',
    },
    information: 'Notes',
    informationPlaceholder: 'Anything you want to note about the visit...',
  },
  fields: {
    visitType: 'Visit type',
    sourceDocument: 'Source document',
    date: 'Visit start date',
    startTime: 'Visit start time',
    endTime: 'Visit end time',
    patientId: 'Patient ID',
    doctor: 'Physician',
    hospital: 'Hospital',
    city: 'City',
    travelType: 'Travel type',
    anyTodo: 'At least one ticked item',
    stockItem: 'At least one material',
    windowOpenDate: 'Window opens',
    targetDate: 'Target date',
    windowCloseDate: 'Window closes',
    clinicianFullName: 'Clinician',
    travelBefore: 'Travel time before',
    travelDistanceBefore: 'Travel distance before (km)',
    visitStartDate: 'Visit start date',
    visitStartTime: 'Visit start time',
    visitEndDate: 'Visit end date',
    visitEndTime: 'Visit end time',
    visitDuration: 'Visit duration',
    travelAfter: 'Travel time after',
    issueOccurred: 'Did an issue occur?',
    issueResultedInMiss: 'Did the issue cause the visit to be missed?',
    issueSource: 'Issue source',
    dateInitialSourceReceived: 'Initial source received',
    dateSourceFinalized: 'Source finalised',
    wasAeSaeTaken: 'AE/SAE taken?',
    dcfCreated: 'DCF created?',
    screenFail: 'Screen fail?',
    dropPatient: 'Drop patient?',
    protocolDeviation: 'Protocol deviation',
    capaOpened: 'CAPA opened?',
    processEndDate: 'Process end date',
    slaTarget: 'SLA target',
    issueCategory: 'Issue category',
    issueDetails: 'Issue details',
  },
  stock: {
    add: 'Add Material',
    count_one: '{{count}} material',
    count_other: '{{count}} materials',
    requiredNote: '“Will a stock entry be made?” is ticked on the to-do list. Add at least one material before completing the visit.',
    requiredDone: 'Stock entry done: materials have been entered for this visit.',
    optionalNote: 'If you didn’t use any material during the visit, you can skip this step.',
    emptyTitle: 'No materials added yet',
    emptyText: 'Record the materials you used during the visit with “Add Material”.',
    columns: {
      name: 'Material',
      type: 'Stock type',
      quantity: 'Quantity used',
      reason: 'Reason',
      addedAt: 'Added on',
      action: 'Action',
    },
    remove: 'Remove',
    confirmRemove: {
      title: 'Remove this material?',
      message: 'Are you sure you want to remove this material? The quantity used is returned to your stock.',
      confirm: 'Yes, remove',
    },
    dialog: {
      title: 'Add Material',
      subtitle: 'Choose the material used for this visit',
      stock: 'Choose stock',
      assignedOnly: 'Only stock assigned to you with a remaining quantity is listed.',
      placeholder: 'Choose stock...',
      none: 'No stock is assigned to you',
      option: '{{name}} - {{type}} (Remaining: {{remaining}})',
      selectedInfo: 'Selected stock',
      stockName: 'Stock name',
      stockType: 'Stock type',
      available: 'Available',
      availableValue: '{{count}} pcs',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      quantity: 'Quantity used',
      quantityPlaceholder: 'Quantity to use',
      reason: 'Reason',
      reasonPlaceholder: 'Why the material was used...',
      add: 'Add',
      adding: 'Adding...',
      cancel: 'Cancel',
    },
  },
  expenses: {
    refresh: 'Refresh',
    refreshHint: 'Reload the expenses',
    add: 'Add Expense',
    total: 'Total',
    count_one: '{{count}} expense',
    count_other: '{{count}} expenses',
    emptyTitle: 'No expenses yet',
    emptyText: 'Record the expenses of this visit together with the receipt or invoice using “Add Expense”.',
    columns: {
      name: 'Name',
      type: 'Type',
      cost: 'Cost',
      info: 'Notes',
      files: 'Files',
      date: 'Date',
    },
    types: {
      transportation: 'Transport',
      accommodation: 'Accommodation',
      meal: 'Meal',
      supplies: 'Supplies',
      other: 'Other',
    },
    amount: '{{value}} TL',
    fileFallback: 'File',
    dialog: {
      title: 'Add Expense',
      subtitle: 'Record the expense together with the receipt or invoice',
      name: 'Expense name',
      namePlaceholder: 'Enter the expense name',
      cost: 'Cost',
      type: 'Expense type',
      info: 'Notes',
      infoPlaceholder: 'Any extra details about the expense...',
      optional: '(optional)',
      files: 'Attach files',
      filesHint: '(invoice, receipt, etc.)',
      drop: 'Drag files here or',
      dropLink: 'click to choose',
      dropHint: 'PNG, JPG, WEBP, PDF - 5 MB at most',
      uploading: 'Uploading files...',
      preview: 'Preview',
      delete: 'Delete',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
    },
  },
  shipments: {
    summary: {
      total: 'Total',
      notSent: 'Not sent',
      shipped: 'In transit',
      delivered: 'Delivered',
      cost: 'Shipping cost',
    },
    add: 'Add Shipment',
    emptyTitle: 'No shipments yet',
    emptyText: 'Track the materials sent for this visit, their tracking numbers and shipping papers here.',
    firstRecord: 'Add the first one',
    edit: 'Edit',
    delete: 'Delete',
    sentOn: 'Sent: {{date}}',
    deliveredOn: 'Delivered: {{date}}',
    costLabel: 'Shipping cost:',
    trackingNo: 'Tracking no.',
    copy: 'Copy the tracking number',
    openCarrier: 'Track on the {{carrier}} website',
    markAs: 'Mark as {{status}}',
    status: {
      not_sent: 'Not Shipped',
      shipped: 'Shipped',
      delivered: 'Delivered',
      returned: 'Returned / Problem',
    },
    carriers: {
      kurye: 'Courier / hand delivery',
      diger: 'Other',
    },
    confirmDelete: {
      title: 'Delete this shipment?',
      message: 'Delete the shipment “{{subject}}”? This can’t be undone.',
      confirm: 'Yes, delete',
    },
    dialog: {
      titleNew: 'New Shipment',
      titleEdit: 'Edit Shipment',
      subtitle: 'Record the parcel, the recipient and the shipping papers',
      sections: {
        details: 'Shipment details',
        status: 'Shipping status',
        recipient: 'Recipient and address',
        files: 'Files',
        note: 'Note',
      },
      subject: 'Subject',
      subjectPlaceholder: 'E.g. Lab kit shipment',
      content: 'Contents',
      contentPlaceholder: 'What is in the box? Material, quantity, lot number...',
      statusHint: 'Dates are filled in when you change the status; you can still edit them.',
      carrier: 'Carrier',
      carrierPlaceholder: 'Select...',
      trackingNumber: 'Tracking number',
      trackingPlaceholder: 'Tracking number',
      shippedAt: 'Shipped on',
      deliveredAt: 'Delivered on',
      cost: 'Shipping cost',
      costPlaceholder: '0.00',
      recipientName: 'Sent to',
      recipientNamePlaceholder: 'Full name',
      recipientOrg: 'Organisation / centre',
      recipientOrgPlaceholder: 'Hospital, centre or company',
      recipientPhone: 'Recipient phone',
      recipientPhonePlaceholder: '05xx xxx xx xx',
      senderName: 'Sender',
      senderNamePlaceholder: 'Who sent it',
      destination: 'Sent to (address)',
      destinationPlaceholder: 'Delivery address',
      drop: 'Drag the shipping receipt, waybill or delivery note here, or click to choose',
      uploading: 'Uploading...',
      dropHint: 'PDF, image, Word or Excel · 10 MB at most',
      removeFile: 'Delete file',
      note: 'Note',
      notePlaceholder: 'Anything you want to add',
      cancel: 'Cancel',
      save: 'Save',
      update: 'Update',
      saving: 'Saving...',
    },
  },
  evaluation: {
    summary: {
      visitType: 'Visit type',
      duration: 'Visit duration',
      durationValue: '{{value}} h',
      cycleTime: 'Cycle time',
      sla: 'SLA',
      slaMet: 'Met',
      slaOverdue: 'Overdue',
    },
    home: {
      A: { title: 'Identity', hint: 'The patient ID and visit number come from the Visit Form.' },
      B: { title: 'Window / Timing', hint: 'The opening, target and closing dates of the visit window per protocol.' },
      C: { title: 'Planned', hint: 'The planned date and time of the visit and the clinician who goes.' },
      D: { title: 'Travel Before', hint: 'Time (hours and minutes) and distance travelled to the visit.' },
      E: { title: 'Visit Details', hint: 'When the visit actually started and ended, and its total duration.' },
      F: { title: 'Travel After', hint: 'Time spent travelling back from the visit.' },
      G: { title: 'Issue Information', hint: 'If there was an issue, give its source and whether it caused the visit to be missed.' },
      H: { title: 'Quality Review', hint: 'Visible to managers only: correction requests and their reasons.' },
      I: { title: 'Source Dates', hint: 'When the source document was first received, last revision requested and finalised.' },
      J: { title: 'Outcome', hint: 'Whether an AE/SAE was taken and a DCF was created at the end of the visit.' },
    },
    site: {
      A: { title: 'Visit Summary', hint: 'A summary of the project and visit. You can correct the patient ID here if needed.' },
      B: { title: 'Patient Status', hint: 'Whether the patient failed screening or dropped out of the study.' },
      C: { title: 'Protocol & CAPA', hint: 'If there was a protocol deviation, state whether a CAPA was opened and add your notes.' },
      D: { title: 'Completion Dates', hint: 'When the eCRF, source document and signatures were completed.' },
      E: { title: 'Process & SLA', hint: 'Process end date and SLA target. Cycle time and the SLA result are calculated automatically.' },
      F: { title: 'Issue Tracking', hint: 'If there was an issue, give its category and the details.' },
    },
    optionalSection: 'This section doesn’t block completing the visit. Dates left empty are marked as “missing” on the visit, and the warning disappears once you enter them.',
    adminOnly: 'Managers only',
    fields: {
      patientId: 'Patient ID',
      visitId: 'Visit ID',
      windowOpenDate: 'Window opens',
      targetDate: 'Target date',
      windowCloseDate: 'Window closes',
      clinician: 'Clinician',
      clinicianPlaceholder: 'Clinician’s full name',
      plannedDate: 'Planned date',
      plannedTime: 'Planned time',
      travelTime: 'Time',
      distance: 'Distance (km)',
      visitStartDate: 'Visit start date',
      visitStartTime: 'Visit start time',
      visitEndDate: 'Visit end date',
      visitEndTime: 'Visit end time',
      visitDuration: 'Visit duration',
      issueOccurred: 'Did an issue occur?',
      issueResultedInMiss: 'Did the issue cause the visit to be missed?',
      issueSource: 'Issue source',
      issueSourcePlaceholder: 'Source of the issue...',
      smoCorrectionRequests: 'SMO correction requests',
      smoCorrectionRequestsPlaceholder: 'Number of correction requests',
      qualityManagerCheck: 'Clinical PM corrections',
      qualityManagerCheckPlaceholder: 'Clinical PM corrections...',
      correctionReason: 'Reason for correction',
      correctionReasonPlaceholder: 'Reason for the correction...',
      dateInitialSourceReceived: 'Initial source received',
      dateLatestSourceRevision: 'Latest revision request from CPM',
      dateSourceFinalized: 'Source finalised',
      wasAeSaeTaken: 'AE/SAE taken?',
      dcfCreated: 'DCF created?',
      projectName: 'Project name',
      centerId: 'Centre ID',
      visitType: 'Visit type',
      siteVisitType: 'Hospital (Site)',
      siteCoordinator: 'Site coordinator',
      sitePatientPlaceholder: 'Enter the patient ID...',
      visitDate: 'Visit date',
      screenFail: 'Screen fail?',
      dropPatient: 'Drop patient?',
      protocolDeviation: 'Was there a protocol deviation?',
      capaOpened: 'CAPA opened?',
      comments: 'Comments',
      commentsPlaceholder: 'Notes and comments about the visit...',
      ecrf: 'eCRF',
      sourceDoc: 'Source document',
      signature: 'Signature',
      completionDate: 'Completion date',
      completionTime: 'Completion time',
      sourceDocCompleted: 'Source document completed?',
      sourceDocCompletionTime: 'Source doc. completion time',
      processEndDate: 'Process end date',
      cycleTime: 'Cycle time (days)',
      slaTarget: 'SLA target (days)',
      slaTargetPlaceholder: 'Target number of days',
      slaMet: 'SLA met?',
      slaOverdue: 'SLA overdue?',
      autoCalculated: 'Calculated automatically',
      issueCategory: 'Issue category',
      issueDetails: 'Issue details',
      issueDetailsPlaceholder: 'Details of the issue...',
    },
    issueCategories: {
      iv_access: 'IV access',
      patient_compliance: 'Patient compliance',
      documentation: 'Documentation',
      logistics: 'Logistics',
      protocol: 'Protocol',
      equipment: 'Equipment',
      other: 'Other',
    },
  },
  cra: {
    status: 'Status: {{state}}',
    sent: 'Sent',
    notSent: 'Not sent',
    expenseWarning_one: 'This visit has {{count}} expense. Choosing a CRA is required.',
    expenseWarning_other: 'This visit has {{count}} expenses. Choosing a CRA is required.',
    cra: 'CRA',
    selectRequired: 'Choose a CRA (required)',
    select: 'Please select',
    send: 'Send',
    resend: 'Send Again',
    sending: 'Sending...',
  },
};

export default visitModal;
